/**
 * Manuscript paragraph boundaries: TipTap serializes blank lines as NBSP-only paragraphs;
 * the editor wire uses `\n` between blocks. Shared locate/verify helpers keep both views aligned.
 */

/** Line is a manuscript spacer paragraph (empty, whitespace-only, or NBSP-only). */
export function isManuscriptSpacerLine(line: string): boolean {
  const t = String(line ?? "")
    .replace(/[\u00a0\u200b-\u200d\ufeff]/g, " ")
    .trim();
  return t.length === 0;
}

/** Remove spacer-only lines while preserving paragraph breaks between content lines. */
export function stripManuscriptSpacerLines(text: string): string {
  return String(text ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .filter((line) => !isManuscriptSpacerLine(line))
    .join("\n");
}

/**
 * Split wire/editor passage text into visible paragraphs.
 * PM `textBetween` uses `\n` between textblocks; markdown may use `\n\n` or spacer lines.
 */
export function splitManuscriptWireParagraphs(text: string): string[] {
  return stripManuscriptSpacerLines(text)
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * One or more line breaks, optionally containing spacer-only lines (NBSP / whitespace).
 * Indices stay aligned with the original haystack string.
 */
export const MANUSCRIPT_PARAGRAPH_GAP_PATTERN = "\\r?\\n(?:[ \\t\\u00a0]*\\r?\\n)+";

export function buildManuscriptSpacerAwareParagraphPattern(needle: string): string | null {
  const paragraphs = splitManuscriptWireParagraphs(needle);
  if (paragraphs.length < 2) return null;
  return paragraphs.map(escapeRegExp).join(MANUSCRIPT_PARAGRAPH_GAP_PATTERN);
}

export type ManuscriptParagraphMatchRange = { start: number; end: number };

function chooseBestParagraphMatch(
  matches: RegExpExecArray[],
  hintOffset?: number,
): RegExpExecArray | null {
  if (matches.length === 0) return null;
  let chosen = matches[0]!;
  if (matches.length > 1 && hintOffset !== undefined) {
    let bestDist = Math.abs(chosen.index - hintOffset);
    for (let i = 1; i < matches.length; i += 1) {
      const candidate = matches[i]!;
      const dist = Math.abs(candidate.index - hintOffset);
      if (dist < bestDist) {
        chosen = candidate;
        bestDist = dist;
      }
    }
  }
  return chosen;
}

/**
 * Locate a multi-paragraph needle when the haystack uses NBSP spacer lines or extra blank lines.
 */
export function findManuscriptSpacerAwareParagraphMatch(
  haystack: string,
  needle: string,
  hintOffset?: number,
): ManuscriptParagraphMatchRange | null {
  const pattern = buildManuscriptSpacerAwareParagraphPattern(needle);
  if (!pattern) return null;
  const re = new RegExp(pattern, "g");
  const matches: RegExpExecArray[] = [];
  let match: RegExpExecArray | null;
  while ((match = re.exec(haystack)) !== null) {
    matches.push(match);
  }
  const chosen = chooseBestParagraphMatch(matches, hintOffset);
  if (!chosen) return null;
  return { start: chosen.index, end: chosen.index + chosen[0].length };
}

/**
 * Find the needle inside a haystack slice when literal `indexOf` misses due to spacer lines.
 */
export function findPassageRangeInHaystackSlice(
  haystack: string,
  sliceStart: number,
  sliceEnd: number,
  needle: string,
  hintOffset?: number,
): ManuscriptParagraphMatchRange | null {
  const slice = haystack.slice(sliceStart, sliceEnd).replace(/\r\n/g, "\n");
  const needleCore = String(needle ?? "").replace(/\r\n/g, "\n").trim();
  if (!needleCore) return null;

  const absoluteHint =
    hintOffset !== undefined ? Math.max(0, hintOffset - sliceStart) : undefined;

  let found = slice.indexOf(needleCore);
  if (found >= 0) {
    return {
      start: sliceStart + found,
      end: sliceStart + found + needleCore.length,
    };
  }

  const spacerMatch = findManuscriptSpacerAwareParagraphMatch(slice, needleCore, absoluteHint);
  if (spacerMatch) {
    return {
      start: sliceStart + spacerMatch.start,
      end: sliceStart + spacerMatch.end,
    };
  }

  const paragraphs = splitManuscriptWireParagraphs(needleCore);
  if (paragraphs.length === 1) {
    const single = paragraphs[0]!;
    found = slice.indexOf(single);
    if (found >= 0) {
      return {
        start: sliceStart + found,
        end: sliceStart + found + single.length,
      };
    }
  }

  return null;
}
